import { BaseApiClient } from '../client'
import type { SeedingProduct, SeedingStatus, PaginationMeta } from '../types'

export interface PublicTier {
  name: string
  slug: string
  position: number
  commissionRate: number
  monthlySeedings: number
  minSalesCount: number
}

export interface PublicCreatorData {
  id: string
  name: string
  niche: string | null
  instagramUsername: string | null
  tiktokUsername: string | null
  totalSales30Days: number
  totalSales: number
  approvedSales: number
  pendingSales: number
  availableBalance: number
  pendingBalance: number
  tierOverride: boolean
  commissionTier: PublicTier | null
}

export interface PublicCreatorSale {
  id: string
  orderReference: string
  orderAmount: number
  commissionAmount: number
  commissionRate: number
  status: 'pending' | 'approved' | 'cancelled' | 'paid'
  source: 'guru' | 'yampi' | 'shopify'
  couponUsed: string | null
  warrantyEndsAt: string | null
  approvedAt: string | null
  createdAt: string
}

export interface PublicSeeding {
  id: string
  products: SeedingProduct[]
  status: SeedingStatus
  trackingCode: string | null
  trackingUrl: string | null
  carrier: string | null
  shippedAt: string | null
  estimatedDeliveryAt: string | null
  deliveredAt: string | null
  createdAt: string
}

export class PublicCreatorApi extends BaseApiClient {
  async getByToken(token: string): Promise<PublicCreatorData> {
    const url = `${this.baseUrl}/api/v1/creators/${token}`
    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
    })
    const result = (await response.json()) as PublicCreatorData & {
      error?: { code: string; message: string }
    }
    if (!response.ok || result.error) {
      throw new Error(result.error?.message || 'Erro desconhecido')
    }
    return result
  }

  async getSales(
    token: string,
    page = 1,
    limit = 10
  ): Promise<{ data: PublicCreatorSale[]; meta: PaginationMeta }> {
    const url = `${this.baseUrl}/api/v1/creators/${token}/sales?page=${page}&limit=${limit}`
    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
    })
    const result = (await response.json()) as {
      data: PublicCreatorSale[]
      meta: PaginationMeta
      error?: { code: string; message: string }
    }
    if (!response.ok || result.error) {
      throw new Error(result.error?.message || 'Erro desconhecido')
    }
    return { data: result.data, meta: result.meta }
  }

  async getTiers(token: string): Promise<PublicTier[]> {
    const url = `${this.baseUrl}/api/v1/creators/${token}/tiers`
    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
    })
    const result = (await response.json()) as {
      data: PublicTier[]
      error?: { code: string; message: string }
    }
    if (!response.ok || result.error) {
      throw new Error(result.error?.message || 'Erro desconhecido')
    }
    return result.data
  }

  async getSeedings(token: string): Promise<PublicSeeding[]> {
    const url = `${this.baseUrl}/api/v1/creators/${token}/seedings`
    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
    })
    const result = (await response.json()) as {
      data: PublicSeeding[]
      error?: { code: string; message: string }
    }
    if (!response.ok || result.error) {
      throw new Error(result.error?.message || 'Erro desconhecido')
    }
    return result.data
  }
}
